import React, { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { isErrorResponse, isUuidString } from '@utils/utils';
import {
    PublicTransitLayerEntityName,
    PublicTransitRouteEntity,
} from '@api/entities/replancity_PublicTransitLine';
import { useEntityLoader } from '../../../entities/entity-form/model/useEntityLoader';
import { EntityView } from '@api/entityApi';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName, selectSourcesById } from '@redux/selectors/selectors';
import { setSourceReloadRequired } from '@redux/map/map-reducer';
import { clearedSelectedMapFeatures } from '@redux/showInfo/showInfoReducer';
import EntityManagerCleaner from '../../../entities/EntityManagerCleaner';
import { useMapFiltersContext } from '@context/mapFiltersContext';
import mapApi from '@api/mapApi';
import { EntityServiceName } from '@api/enums/enums';
import { createLabelsLayer } from '@utils/mapUtils';
import { Anchor, SymbolLayer } from 'mapbox-gl';

const TransitRoute = React.memo(() => {
    const entityName = PublicTransitLayerEntityName.ROUTES;
    const stopsEntityName = PublicTransitLayerEntityName.STOPS;
    const entityView: EntityView = 'full';

    const newSourceId = 'route-stops-numbering';

    const { mapboxMapId } = useMap();

    const dispatch = useDispatch();
    const routesLayerId: string = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.ROUTES)
    );
    const sourcesById = useTypedSelector(selectSourcesById);

    const stopsLayerId: string = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, stopsEntityName)
    );
    const stopsSource = sourcesById[stopsLayerId];

    const { routeId } = useParams<any>();
    const existingRouteId = useMemo(() => (isUuidString(routeId) ? routeId : ''), [routeId]);

    const { fullEntity, loadData } = useEntityLoader<PublicTransitRouteEntity>({ entityName });

    const { highlightLayerFeatures } = useMapFiltersContext();

    const { defaultRouteProfile, defaultSchedule } = fullEntity;
    const { id: routeProfileId } = defaultRouteProfile ?? {};
    const { id: scheduleId } = defaultSchedule ?? {};

    useEffect(() => {
        return () => {
            try {
                const map = mapboxMapId?.getMap();
                if (!map) {
                    return;
                }

                if (map.isStyleLoaded() && map?.getLayer(newSourceId)) {
                    map?.removeLayer(newSourceId);
                }
            } catch (e) {
                console.error(
                    "Replan. TransitRoute: for the moment it's not clear how to fix this issue, therefore it's intentionally wrapped by try catch"
                );
            }

            highlightLayerFeatures({
                layerId: routesLayerId,
                mapboxLayerType: 'line',
            });

            dispatch(clearedSelectedMapFeatures());
        };
    }, [routesLayerId, mapboxMapId, newSourceId, highlightLayerFeatures]);

    const highlightRoute = useCallback(async () => {
        if (existingRouteId && routesLayerId && stopsLayerId) {
            const abortController = new AbortController();

            const resp = await mapApi.getListRelatedFeatures(
                {
                    sourceId: existingRouteId,
                    sourceEntityType: entityName,
                    targetEntityType: PublicTransitLayerEntityName.STOPS,
                },
                EntityServiceName.PUBLIC_TRANSIT,
                abortController.signal
            );

            if (!isErrorResponse(resp)) {
                const { featureId, relatedFeatures } = resp;

                const featureStateIdByPositon = relatedFeatures.reduce((acc, cur, ind) => {
                    acc[cur.toString()] = ++ind;
                    return acc;
                }, {});

                const symbolLayer = () => {
                    const layer = createLabelsLayer(stopsSource);
                    return {
                        ...layer,
                        source: stopsLayerId,
                        id: newSourceId,
                        layerName: newSourceId,
                        layout: {
                            ...layer.layout,

                            'text-offset': [0, 1.25],
                            'text-anchor': 'top' as Anchor,
                            'text-field': [
                                'get',
                                ['get', 'featureStateId'],
                                ['literal', featureStateIdByPositon],
                            ],
                        },
                        paint: {
                            ...layer.paint,
                            'text-color': '#fff',
                            'text-halo-color': '#db08e8',
                        },
                        filter: [
                            'in',
                            ['get', 'featureStateId'],
                            ['literal', relatedFeatures.map(id => id.toString())],
                        ],
                    };
                };

                highlightLayerFeatures({
                    layerId: routesLayerId,
                    mapboxLayerType: 'line',
                    featureStateIds: [featureId.toString()],
                });
                highlightLayerFeatures({
                    layerId: stopsLayerId,
                    mapboxLayerType: 'circle',
                    featureStateIds: relatedFeatures.map(id => id.toString()),
                });

                const map = mapboxMapId?.getMap();

                if (map?.getLayer(newSourceId)) {
                    map?.removeLayer(newSourceId);
                }

                map?.addLayer(symbolLayer() as SymbolLayer);
            }
        }
    }, [
        existingRouteId,
        entityName,
        routesLayerId,
        stopsLayerId,
        stopsSource,
        newSourceId,
        mapboxMapId,
        dispatch,
        highlightLayerFeatures,
    ]);

    const loadRoute = useCallback(
        async (reloadLayer = false) => {
            const map = mapboxMapId?.getMap();
            if (map) {
                map.scrollZoom.disable();
            }

            await loadData({ entityName, entityId: existingRouteId, entityView });

            //TODO remove when it's not required to reload after stop addition
            if (reloadLayer && routesLayerId) {
                dispatch(
                    setSourceReloadRequired({ sourceId: routesLayerId, isReloadRequired: true })
                );
            }

            if (map) {
                map.scrollZoom.enable();
            }
        },
        [
            loadData,
            entityName,
            entityView,
            existingRouteId,
            routesLayerId,
            mapboxMapId,
            highlightRoute,
        ]
    );

    useEffect(() => {
        void (async function () {
            await loadRoute();
            await highlightRoute();
        })();
    }, [loadRoute, highlightRoute]);

    return (
        <>
            <EntityManagerCleaner entityName={entityName}>
                <Outlet context={{ routeProfileId, scheduleId, loadRoute }} />
            </EntityManagerCleaner>
        </>
    );
});

export default TransitRoute;
